import React, { useState } from 'react';
import { format, parseISO } from 'date-fns';

const DISCIPLINE_COLORS = {
  sea_kayaking: { bg: 'var(--sea-bg)', color: 'var(--sea)' },
  whitewater:   { bg: 'var(--ww-bg)',  color: 'var(--ww)' },
  canoe:        { bg: 'var(--canoe-bg)', color: 'var(--canoe)' },
  surfski:      { bg: 'var(--surfski-bg)', color: 'var(--surfski)' },
  sup:          { bg: 'var(--sup-bg)', color: 'var(--sup)' },
  other:        { bg: 'var(--other-bg)', color: 'var(--other)' },
};

function fmt(dateStr) {
  if (!dateStr) return '—';
  try { return format(parseISO(dateStr), 'MMM d, yyyy'); }
  catch { return dateStr; }
}

function HeartBtn({ captureId, likedIds, onToggleLike }) {
  const isLiked = likedIds?.has(Number(captureId));
  return (
    <button
      className={`heart-btn ${isLiked ? 'heart-liked' : ''}`}
      onClick={e => { e.stopPropagation(); onToggleLike(captureId); }}
      title={isLiked ? 'Unlike this event' : 'Like this event'}
    >
      {isLiked ? '❤️' : '🤍'}
    </button>
  );
}

function SortTh({ col, sortBy, sortDir, onSort, label }) {
  const active = sortBy === col;
  return (
    <th className="sortable-th">
      <div className="sort-th-inner">
        <span className="sort-th-label">{label}</span>
        <button
          className={`sort-widget ${active ? 'sort-active' : ''}`}
          onClick={() => onSort(col)}
          title={active ? (sortDir === 'asc' ? 'Sort descending' : 'Sort ascending') : `Sort by ${label}`}
        >
          <span className={`sort-up ${active && sortDir === 'asc' ? 'sort-on' : ''}`}>▲</span>
          <span className={`sort-down ${active && sortDir === 'desc' ? 'sort-on' : ''}`}>▼</span>
        </button>
      </div>
    </th>
  );
}

function ColHeaders({ sortBy, sortDir, onSort }) {
  return (
    <tr>
      <th style={{width:'40px'}} />
      <SortTh col="name"       label="Event"       sortBy={sortBy} sortDir={sortDir} onSort={onSort} />
      <SortTh col="outfit"     label="Outfitter"   sortBy={sortBy} sortDir={sortDir} onSort={onSort} />
      <SortTh col="discipline" label="Discipline"  sortBy={sortBy} sortDir={sortDir} onSort={onSort} />
      <SortTh col="type"       label="Type"        sortBy={sortBy} sortDir={sortDir} onSort={onSort} />
      <SortTh col="location"   label="Location"    sortBy={sortBy} sortDir={sortDir} onSort={onSort} />
      <SortTh col="date"       label="Date"        sortBy={sortBy} sortDir={sortDir} onSort={onSort} />
      <th>Cost</th>
    </tr>
  );
}

function OppRow({ opp, onEventClick, likedIds, onToggleLike, muted }) {
  const disc = DISCIPLINE_COLORS[opp.opportunity_class] || DISCIPLINE_COLORS.other;
  return (
    <tr className={`list-row ${muted ? 'list-row-muted' : ''}`} onClick={() => onEventClick(opp)}>
      <td onClick={e => e.stopPropagation()} style={{padding:'0 8px'}}>
        <HeartBtn captureId={opp.capture_id} likedIds={likedIds} onToggleLike={onToggleLike} />
      </td>
      <td className="list-name">{opp.opportunity_name || '—'}</td>
      <td>{opp.outfit_name || '—'}</td>
      <td>
        <span className="list-badge" style={{ background: disc.bg, color: disc.color }}>
          {opp.opportunity_class?.replace('_', ' ') || 'other'}
        </span>
      </td>
      <td><span className="list-type">{opp.opportunity_type}</span></td>
      <td className="list-location">
        {[opp.address_city, opp.address_state].filter(Boolean).join(', ') || '—'}
        {opp.address_country && <span className="list-country">{opp.address_country}</span>}
      </td>
      <td className="list-date">
        {opp.event_start_date ? (
          <>
            <div>{fmt(opp.event_start_date)}</div>
            {opp.event_end_date && opp.event_end_date !== opp.event_start_date && (
              <div className="list-date-end">→ {fmt(opp.event_end_date)}</div>
            )}
          </>
        ) : (
          <span style={{ color: 'var(--text-muted)', fontStyle: 'italic' }}>On request</span>
        )}
      </td>
      <td className="list-cost">{opp.cost_text || '—'}</td>
    </tr>
  );
}

export default function ListView({ opportunities, onEventClick, likedIds, onToggleLike }) {
  const safeOpps = Array.isArray(opportunities) ? opportunities : [];
  const [sortBy, setSortBy] = useState('date');
  const [sortDir, setSortDir] = useState('asc');

  const datedOpps = safeOpps.filter(o => o.event_start_date);
  const undatedOpps = safeOpps.filter(o => !o.event_start_date);

  const sortFn = (a, b) => {
    let cmp = 0;
    switch (sortBy) {
      case 'date':       cmp = (a.event_start_date || '').localeCompare(b.event_start_date || ''); break;
      case 'name':       cmp = (a.opportunity_name || '').localeCompare(b.opportunity_name || ''); break;
      case 'outfit':     cmp = (a.outfit_name || '').localeCompare(b.outfit_name || ''); break;
      case 'discipline': cmp = (a.opportunity_class || '').localeCompare(b.opportunity_class || ''); break;
      case 'type':       cmp = (a.opportunity_type || '').localeCompare(b.opportunity_type || ''); break;
      case 'location':   cmp = (a.address_state || '').localeCompare(b.address_state || ''); break;
      default:           cmp = 0;
    }
    return sortDir === 'asc' ? cmp : -cmp;
  };

  const sortedDated = [...datedOpps].sort(sortFn);
  const sortedUndated = [...undatedOpps].sort(sortFn);
  const allSorted = [...sortedDated, ...sortedUndated];

  const toggleSort = (col) => {
    if (sortBy === col) {
      setSortDir(d => d === 'asc' ? 'desc' : 'asc');
    } else {
      setSortBy(col);
      setSortDir('asc');
    }
  };

  if (!safeOpps.length) {
    return <div className="empty-state"><div className="empty-icon">🌊</div><h3>No events match your filters</h3></div>;
  }

  return (
    <div className="list-view">
      <div className="list-count">
        {safeOpps.length} event{safeOpps.length === 1 ? '' : 's'}
        <span className="list-sort-hint">
          Sorted by <strong>{sortBy}</strong> {sortDir === 'asc' ? '↑' : '↓'}
        </span>
        {undatedOpps.length > 0 && (
          <span className="list-undated-note">{undatedOpps.length} undated shown at bottom</span>
        )}
      </div>
      <div className="list-table-wrap">
        <table className="list-table">
          <thead>
            <ColHeaders sortBy={sortBy} sortDir={sortDir} onSort={toggleSort} />
          </thead>
          <tbody>
            {allSorted.map(opp => (
              <OppRow
                key={opp.capture_id}
                opp={opp}
                onEventClick={onEventClick}
                likedIds={likedIds}
                onToggleLike={onToggleLike}
                muted={!opp.event_start_date}
              />
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}
